import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient, getHttpStatus } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import {
    CustomPropertyType, CustomPropertyUsage, EnumOptionUsage, UiConfig,
} from '@evidentid/tprm-portal-lib/models/dashboard';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { RpConfigStatus, getDashboardActions, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';
import {
    mutateCommonStatusFailState,
    mutateCommonStatusFinishState,
    mutateCommonStatusStartState,
} from '@/utils/vuex-common/commonStatusMutations';
import { performCommonCudAction } from '@/utils/vuex-common/performCommonCudAction';

export interface DashboardConfigurationRequirement {
    gatewayClient: GatewayApiClient;
    logger?: Logger<any>;
}

interface DashboardConfigurationState {
    updateDisplayStatus: Record<string, OperationStatus>;
    enumFieldUsageCounts: Record<string, Record<string, EnumOptionUsage>>;
    enumFieldUsageStatus: Record<string, OperationStatus>;
    customPropertyUsage: Record<string, CustomPropertyUsage>;
    customPropertyUsageStatus: Record<string, OperationStatus>;
}

export const useDashboardConfigurationStore = defineStore('dashboardConfiguration', {
    state: (): DashboardConfigurationState => ({
        updateDisplayStatus: {},
        enumFieldUsageCounts: {},
        enumFieldUsageStatus: {},
        customPropertyUsage: {},
        customPropertyUsageStatus: {},
    }),
    actions: {
        startUpdatingDisplayConfig(payload: { rpName: string }) {
            mutateCommonStatusStartState(this, 'updateDisplayStatus', payload);
        },
        finishUpdatingDisplayConfig(payload: { rpName: string }) {
            mutateCommonStatusFinishState(this, 'updateDisplayStatus', payload);
        },
        failUpdatingDisplayConfig(payload: { rpName: string }) {
            mutateCommonStatusFailState(this, 'updateDisplayStatus', payload);
        },
        setEnumFieldUsageCounts(payload: { fieldId: string, counts: Record<string, EnumOptionUsage> }) {
            this.enumFieldUsageCounts = { ...this.enumFieldUsageCounts, [payload.fieldId]: payload.counts };
        },
        setEnumFieldUsageStatus(payload: { fieldId: string, status: OperationStatus }) {
            this.enumFieldUsageStatus = { ...this.enumFieldUsageStatus, [payload.fieldId]: payload.status };
        },
        async updateDisplayConfig(payload: { rpName: string, uiConfig: UiConfig }) {
            const { rpName, uiConfig } = payload;
            const { gatewayClient, logger } = this.$app as DashboardConfigurationRequirement;

            const rpConfig = useDashboardStore().rpConfig[rpName] as RpConfigStatus | undefined;
            if (rpConfig?.status !== OperationStatus.success) {
                // customProperties isn't trustworthy yet (still loading, errored, or was just
                // cleared) — bail rather than derive/save against a stale or incomplete list.
                // Skip mutating when another save for this rp is already in flight: committing
                // `error` here would stomp its `loading` status and trip performCommonCudAction's
                // own race guard, leaving updateRpConfigDisplay unrun even though that save succeeds.
                if (this.updateDisplayStatus[rpName] !== OperationStatus.loading) {
                    this.failUpdatingDisplayConfig({ rpName });
                    logger?.warning(
                        `updateDisplayConfig bailed out for rp "${rpName}": rpConfig status is ` +
                            `"${rpConfig?.status ?? 'missing'}", not success`,
                    );
                }
                return;
            }

            // Derive customPropertiesOrder from the full current property list rather than
            // trusting the incoming order as-is: this restores any legitimate property missing
            // from the incoming order, drops stale ids left over from a property removed
            // outside the app, and de-dupes — the same invariant the BE enforces.
            const { customProperties } = rpConfig;
            const orderKeys = getSortedCustomProperties(customProperties, uiConfig.customPropertiesOrder)
                .map((p) => p.key);
            const displayKeys = orderKeys.filter((key) => uiConfig.entitiesTableCustomProperties.includes(key));

            // PUT expects UUIDs — map keys → UUIDs using customProperties in rpConfig
            const keyToUuid = new Map(customProperties.map((p) => [ p.key, p.id ] as [ string, string ]));
            const toUuids = (keys: string[]) => keys.flatMap((key) => {
                const id = keyToUuid.get(key);
                return id ? [ id ] : [];
            });
            const uiConfigWithUuids: UiConfig = {
                customPropertiesOrder: toUuids(orderKeys),
                entitiesTableCustomProperties: toUuids(displayKeys),
            };

            const mutateStart = () => this.startUpdatingDisplayConfig({ rpName });
            const mutateFinish = () => this.finishUpdatingDisplayConfig({ rpName });
            const mutateFail = () => this.failUpdatingDisplayConfig({ rpName });
            const actionCall = () => gatewayClient.tprmUi().updateUiConfig(rpName, uiConfigWithUuids);
            // Scope shim: performCommonCudAction reads scope.state[...] (live) and
            // getPersistingErrorActions() needs the legacy dispatch (the
            // persisting-error module is still Vuex).
            await performCommonCudAction(
                { state: this },
                'updateDisplayStatus', rpName, actionCall, mutateStart, mutateFinish, mutateFail,
            );
            // Keep rpConfig in sync with the saved display settings
            if (this.updateDisplayStatus[rpName] === OperationStatus.success) {
                await getDashboardActions().updateRpConfigDisplay({
                    rpName,
                    customPropertiesOrder: orderKeys,
                    entitiesTableCustomProperties: displayKeys,
                });
            }
        },
        clearUpdateDisplayStatus(payload: { rpName: string }) {
            this.updateDisplayStatus = omit(this.updateDisplayStatus, [ payload.rpName ]);
        },
        async createCustomProperty(payload: {
            rpName: string;
            name: string;
            description: string;
            fieldType: CustomPropertyType;
            required: boolean;
            enumOptions?: string[];
        }) {
            const { rpName, name, description, fieldType, required, enumOptions } = payload;
            const { gatewayClient } = this.$app as DashboardConfigurationRequirement;
            const dashboardActions = getDashboardActions();
            const response = await gatewayClient.tprmUi().createTprmCustomProperty(rpName, {
                name,
                required,
                type: fieldType,
                description: description || null,
                ...(enumOptions != null && { enumOptions }),
            });
            if (response.failureCount > 0) {
                throw new Error(response.failures[0]?.error?.message || 'Failed to create custom property');
            }
            // Clear and reload to get the new property's id and key from the server.
            await dashboardActions.clearRpConfig({ rpName });
            await dashboardActions.loadRpConfig({ rpName });

            const newKey = response.successes?.[0]?.key;
            const rpConfig = useDashboardStore().rpConfig[rpName];
            if (newKey && rpConfig) {
                // updateDisplayConfig derives the full order itself (restoring/deduping as
                // needed), so passing the current persisted order through is enough — no need
                // to compute it here too.
                await this.updateDisplayConfig({
                    rpName,
                    uiConfig: {
                        customPropertiesOrder: rpConfig.customPropertiesOrder,
                        entitiesTableCustomProperties: rpConfig.entitiesTableCustomProperties,
                    },
                });
                // If the PUT failed, re-fetch to restore the true persisted order.
                if (this.updateDisplayStatus[rpName] === OperationStatus.error) {
                    this.clearUpdateDisplayStatus({ rpName });
                    await dashboardActions.clearRpConfig({ rpName });
                    await dashboardActions.loadRpConfig({ rpName });
                }
            }
        },
        async updateCustomProperty(payload: {
            rpName: string;
            propertyId: string;
            name: string;
            description: string;
            required: boolean;
            enumOptions?: string[];
        }) {
            const { rpName, propertyId, name, description, required, enumOptions } = payload;
            const { gatewayClient } = this.$app as DashboardConfigurationRequirement;
            try {
                await gatewayClient.tprmUi().updateTprmCustomProperty(rpName, propertyId, {
                    name,
                    required,
                    description: description || null,
                    ...(enumOptions != null && { enumOptions }),
                });
            } catch (error: any) {
                if (error instanceof Error) {
                    throw error;
                }
                // 4xx rejections from the gateway client are the parsed error body
                // ({ type, message }), not an Error — normalize so callers can show the reason.
                throw new Error(
                    (typeof error?.message === 'string' && error.message) || 'Failed to update custom property',
                );
            }
            const dashboardActions = getDashboardActions();
            await dashboardActions.clearRpConfig({ rpName });
            await dashboardActions.loadRpConfig({ rpName });
        },
        async loadEnumFieldOptionsUsage(payload: { rpName: string, fieldId: string }) {
            const { rpName, fieldId } = payload;
            const { gatewayClient } = this.$app as DashboardConfigurationRequirement;
            this.setEnumFieldUsageStatus({ fieldId, status: OperationStatus.loading });
            try {
                const counts = await gatewayClient.tprmUi().getEnumFieldOptionsUsage(rpName, fieldId);
                this.setEnumFieldUsageCounts({ fieldId, counts });
                this.setEnumFieldUsageStatus({ fieldId, status: OperationStatus.success });
            } catch {
                this.setEnumFieldUsageStatus({ fieldId, status: OperationStatus.error });
            }
        },
        clearEnumFieldUsage(payload: { fieldId: string }) {
            this.enumFieldUsageCounts = omit(this.enumFieldUsageCounts, [ payload.fieldId ]);
            this.enumFieldUsageStatus = omit(this.enumFieldUsageStatus, [ payload.fieldId ]);
        },
        setCustomPropertyUsage(payload: { fieldId: string, usage: CustomPropertyUsage }) {
            this.customPropertyUsage = { ...this.customPropertyUsage, [payload.fieldId]: payload.usage };
        },
        setCustomPropertyUsageStatus(payload: { fieldId: string, status: OperationStatus }) {
            this.customPropertyUsageStatus = {
                ...this.customPropertyUsageStatus,
                [payload.fieldId]: payload.status,
            };
        },
        /**
         * Unlike the enum-option usage, this fails closed: with the dependencies unknown we must not
         * offer a delete the backend would only reject.
         */
        async loadCustomPropertyUsage(payload: { rpName: string, fieldId: string }) {
            const { rpName, fieldId } = payload;
            const { gatewayClient } = this.$app as DashboardConfigurationRequirement;
            this.setCustomPropertyUsageStatus({ fieldId, status: OperationStatus.loading });
            try {
                const usage = await gatewayClient.tprmUi().getCustomPropertyUsage(rpName, fieldId);
                this.setCustomPropertyUsage({ fieldId, usage });
                this.setCustomPropertyUsageStatus({ fieldId, status: OperationStatus.success });
            } catch {
                this.setCustomPropertyUsageStatus({ fieldId, status: OperationStatus.error });
            }
        },
        async deleteCustomProperty(payload: { rpName: string, propertyId: string }) {
            const { rpName, propertyId } = payload;
            const { gatewayClient } = this.$app as DashboardConfigurationRequirement;
            try {
                await gatewayClient.tprmUi().deleteTprmCustomProperty(rpName, propertyId);
            } catch (error: any) {
                // A 409 means something started referencing the property since the modal opened. Its
                // body carries the current dependencies, so store them and let the modal re-render
                // from the fresher data rather than what was loaded on open.
                if (getHttpStatus(error) === 409 && error?.usage) {
                    this.setCustomPropertyUsage({ fieldId: propertyId, usage: error.usage });
                    this.setCustomPropertyUsageStatus({ fieldId: propertyId, status: OperationStatus.success });
                    throw error;
                }
                if (error instanceof Error) {
                    throw error;
                }
                throw new Error(
                    (typeof error?.message === 'string' && error.message) || 'Failed to delete custom property',
                );
            }
            const dashboardActions = getDashboardActions();
            await dashboardActions.clearRpConfig({ rpName });
            await dashboardActions.loadRpConfig({ rpName });
        },
        clearCustomPropertyUsage(payload: { fieldId: string }) {
            this.customPropertyUsage = omit(this.customPropertyUsage, [ payload.fieldId ]);
            this.customPropertyUsageStatus = omit(this.customPropertyUsageStatus, [ payload.fieldId ]);
        },
    },
});
